import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileDown, Loader2, Mail, Plane, Plus, Printer, Trash2, Unlock } from "lucide-react";
import api, { errMsg, money, openCheckout } from "@/lib/api";
import { AIRPORTS, airportAddress } from "@/lib/airports";
import { AddressInput } from "@/components/AddressInput";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  driver_name: "", driver_phone: "", driver_email: "", passenger_name: "", trip_date: "",
  pickup_time: "", flight_number: "", airline: "", pickup_address: "", dropoff_address: "",
  driver_pay: "", notes: "", trip_id: "",
};

const Field = ({ label, testId, children, ...rest }) => (
  <div>
    <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-gold">{label}</label>
    {children || (
      <Input data-testid={testId} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" {...rest} />
    )}
  </div>
);

const Line = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-ink-700/60 py-2 print:border-slate-300">
    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    <span className="text-right text-sm text-slate-100 print:text-black">{value || "—"}</span>
  </div>
);

export default function AirportManifests() {
  const { company, user } = useAuth();
  const isDriver = user?.role === "driver";
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [quota, setQuota] = useState(null);
  const [form, setForm] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [side, setSide] = useState("pickup");
  const [airport, setAirport] = useState("");
  const [terminal, setTerminal] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/airport-manifests");
      setItems(data.manifests);
      setQuota(data.quota);
    } catch (e) { setError(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const topupSession = params.get("topup");
  useEffect(() => {
    if (!topupSession) return;
    api.get(`/airport-manifests/topup-status/${topupSession}`)
      .then(({ data }) => {
        setQuota(data.quota);
        if (data.payment_status === "paid") setNotice(`Unlocked ${quota?.topup_manifests || 20} more manifests.`);
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => { params.delete("topup"); setParams(params, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topupSession]);

  const act = async (fn, msg) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await fn();
      await load();
      setNotice(typeof msg === "function" ? msg(res) : msg);
      return res;
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const startNew = async () => {
    setViewing(null);
    const base = { ...EMPTY, trip_date: new Date().toISOString().slice(0, 10) };
    setForm(base);
    if (isDriver) {
      try {
        const { data } = await api.get("/airport-manifests/me");
        setForm({ ...base, ...data });
      } catch (e) {
        console.warn("no saved airport details; leaving the fields blank", e);
      }
    }
  };

  const save = () =>
    act(async () => {
      const payload = { ...form, driver_pay: Number(form.driver_pay || 0) };
      const { data } = form.id
        ? await api.put(`/airport-manifests/${form.id}`, payload)
        : await api.post("/airport-manifests", payload);
      setForm(null);
      setViewing(data.manifest);
      return data;
    }, "Manifest saved.");

  const openManifest = async (id) => {
    setForm(null);
    setError("");
    try {
      const { data } = await api.get(`/airport-manifests/${id}`);
      setViewing(data.manifest);
    } catch (e) { setError(errMsg(e)); }
  };

  const blocked = quota && !quota.can_create;

  if (form) {
    return (
      <div data-testid="airport-manifest-form" className="max-w-3xl space-y-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Airport manifest</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">
            {form.id ? `Edit manifest #${form.number}` : "New airport manifest"}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            The document the airport expects you to carry. No pricing, no dispatch decisions — just the run.
          </p>
        </div>
        {error && <p data-testid="airport-form-error" className="text-sm text-red-400">{error}</p>}

        <div className="space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <Field label="Chauffeur name" testId="am-driver-name" value={form.driver_name}
              onChange={(e) => setForm({ ...form, driver_name: e.target.value })} />
            <Field label="Chauffeur phone" testId="am-driver-phone" value={form.driver_phone}
              onChange={(e) => setForm({ ...form, driver_phone: e.target.value })} placeholder="713 555 0199" />
            <Field label="Chauffeur email" testId="am-driver-email" type="email" value={form.driver_email}
              onChange={(e) => setForm({ ...form, driver_email: e.target.value })} />
          </div>
          <Field label="Passenger name" testId="am-passenger" value={form.passenger_name}
            onChange={(e) => setForm({ ...form, passenger_name: e.target.value })} />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
            <Field label="Date" testId="am-date" type="date" value={form.trip_date}
              onChange={(e) => setForm({ ...form, trip_date: e.target.value })} />
            <Field label="Pickup time" testId="am-time" type="time" value={form.pickup_time}
              onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} />
            <Field label="Airline" testId="am-airline" value={form.airline}
              onChange={(e) => setForm({ ...form, airline: e.target.value })} placeholder="United" />
            <Field label="Flight number" testId="am-flight" value={form.flight_number}
              onChange={(e) => setForm({ ...form, flight_number: e.target.value })} placeholder="UA2201" />
          </div>
          <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Airport picker</p>
              {[["pickup", "Airport pickup"], ["dropoff", "Airport drop-off"]].map(([k, l]) => (
                <button type="button" key={k} data-testid={`am-airport-side-${k}`}
                  onClick={() => setSide(k)}
                  className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${
                    side === k ? "border-gold/50 bg-gold/10 text-gold" : "border-ink-600 text-slate-400"}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select value={airport} onValueChange={(v) => { setAirport(v); setTerminal(""); }}>
                <SelectTrigger data-testid="am-airport" className="h-11 border-ink-600 bg-ink-800 text-slate-100">
                  <SelectValue placeholder="Choose an airport" />
                </SelectTrigger>
                <SelectContent className="max-h-72 border-ink-600 bg-ink-800 text-slate-100">
                  {AIRPORTS.map((a) => (
                    <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={terminal} disabled={!airport}
                onValueChange={(t) => {
                  setTerminal(t);
                  const address = airportAddress(airport, t);
                  setForm((f) => ({ ...f, [side === "pickup" ? "pickup_address" : "dropoff_address"]: address }));
                }}>
                <SelectTrigger data-testid="am-terminal" className="h-11 border-ink-600 bg-ink-800 text-slate-100">
                  <SelectValue placeholder={airport ? "Choose a terminal" : "Pick an airport first"} />
                </SelectTrigger>
                <SelectContent className="max-h-72 border-ink-600 bg-ink-800 text-slate-100">
                  {(AIRPORTS.find((a) => a.code === airport)?.terminals || []).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <AddressInput label="Pickup address" testId="am-pickup" companyId={company?.id}
            value={{ address: form.pickup_address }}
            onChange={(p) => setForm((f) => ({ ...f, pickup_address: p.address }))}
            placeholder="IAH — Terminal C, Houston, TX" />
          <AddressInput label="Drop-off address" testId="am-dropoff" companyId={company?.id}
            value={{ address: form.dropoff_address }}
            onChange={(p) => setForm((f) => ({ ...f, dropoff_address: p.address }))}
            placeholder="The Post Oak Hotel, Houston, TX" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Chauffeur pay ($)" testId="am-pay" type="number" step="0.01" min="0"
              value={form.driver_pay} onChange={(e) => setForm({ ...form, driver_pay: e.target.value })} />
            <Field label="Linked trip (optional)" testId="am-trip-id" value={form.trip_id}
              onChange={(e) => setForm({ ...form, trip_id: e.target.value })} placeholder="Trip id to prefill from" />
          </div>
          {form.trip_id && !form.id && (
            <Button data-testid="am-prefill" variant="outline" disabled={busy}
              onClick={() => act(async () => {
                const { data } = await api.get(`/airport-manifests/prefill/${form.trip_id}`);
                setForm((f) => ({ ...f, ...data, driver_pay: String(data.driver_pay || "") }));
                return data;
              }, "Pulled the details from that trip.")}
              className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
              Prefill from trip
            </Button>
          )}
          <Field label="Notes">
            <Textarea data-testid="am-notes" rows={3} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="am-save" disabled={busy} onClick={save}
              className="h-11 bg-gold px-8 text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save manifest"}
            </Button>
            <Button data-testid="am-cancel" variant="outline" onClick={() => setForm(null)}
              className="h-11 border-ink-600 bg-transparent text-slate-300 hover:text-gold">Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  if (viewing) {
    return (
      <div data-testid="airport-manifest-view" className="max-w-3xl space-y-6">
        <div className="no-print flex flex-wrap gap-3">
          <Button data-testid="am-back" variant="outline" onClick={() => setViewing(null)}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">← All manifests</Button>
          <Button data-testid="am-print" onClick={() => window.print()}
            className="bg-gold text-ink-900 hover:bg-gold-light"><Printer className="mr-2 h-4 w-4" /> Print</Button>
          <Button data-testid="am-pdf" variant="outline" disabled={busy}
            onClick={() => act(async () => {
              const { data } = await api.get(`/airport-manifests/${viewing.id}/pdf`, { responseType: "blob" });
              const href = URL.createObjectURL(data);
              const a = document.createElement("a");
              a.href = href;
              a.download = `airport-manifest-${viewing.number}.pdf`;
              a.click();
              URL.revokeObjectURL(href);
              return data;
            }, "PDF downloaded.")}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            <FileDown className="mr-2 h-4 w-4" /> Download PDF
          </Button>
          <Button data-testid="am-send" variant="outline" disabled={busy}
            onClick={() => act(() => api.post(`/airport-manifests/${viewing.id}/send`).then(async (r) => {
              await openManifest(viewing.id);
              return r;
            }), `Emailed to ${viewing.driver_email}.`)}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">
            <Mail className="mr-2 h-4 w-4" /> Send to chauffeur
          </Button>
          <Button data-testid="am-edit" variant="outline"
            onClick={() => setForm({ ...viewing, driver_pay: String(viewing.driver_pay ?? "") })}
            className="border-ink-600 bg-transparent text-slate-300 hover:text-gold">Edit</Button>
        </div>
        {error && <p data-testid="airport-view-error" className="no-print text-sm text-red-400">{error}</p>}
        {notice && <p data-testid="airport-view-notice" className="no-print text-sm text-emerald-400">{notice}</p>}

        <div className="rounded-xl border border-ink-600 bg-ink-800 p-8 print:bg-white print:text-black">
          <div className="flex items-start justify-between gap-6 border-b border-ink-600 pb-6 print:border-slate-300">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Airport trip manifest</p>
              <h1 className="mt-3 text-2xl font-bold text-slate-50 print:text-black">{company?.name}</h1>
              <p className="mt-1 text-sm text-slate-400">{company?.phone}</p>
              {company?.address && <p className="text-xs text-slate-500">{company.address}</p>}
            </div>
            <div className="text-right">
              {company?.logo_url && <img src={company.logo_url} alt="" className="h-14 w-auto object-contain" />}
              <p className="mt-2 font-mono text-sm text-gold">#{viewing.number}</p>
            </div>
          </div>

          <div className="mt-6 space-y-1">
            <Line label="Chauffeur" value={viewing.driver_name} />
            <Line label="Chauffeur phone" value={viewing.driver_phone} />
            <Line label="Passenger" value={viewing.passenger_name} />
            <Line label="Date" value={viewing.trip_date} />
            <Line label="Pickup time" value={viewing.pickup_time} />
            <Line label="Flight" value={[viewing.airline, viewing.flight_number].filter(Boolean).join(" ")} />
            <Line label="Pickup" value={viewing.pickup_address} />
            <Line label="Drop-off" value={viewing.dropoff_address} />
            <Line label="Chauffeur pay" value={money(viewing.driver_pay)} />
            <Line label="Notes" value={viewing.notes} />
          </div>
          <p className="mt-6 font-mono text-[11px] text-slate-500">
            Prepared by {viewing.created_by}
            {viewing.sent_at ? ` · emailed to ${viewing.sent_to} on ${viewing.sent_at.slice(0, 16).replace("T", " ")}` : " · not emailed yet"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="airport-manifests-page" className="space-y-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Airport documents</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Airport manifests</h1>
          <p className="mt-2 text-sm text-slate-400">
            Fill one in, print it or email it straight to the chauffeur.{" "}
            {quota && (quota.unlimited
              ? "Unlimited on your plan."
              : `${quota.remaining} of ${quota.allowance} left this month.`)}
          </p>
        </div>
        <Button data-testid="am-new" disabled={blocked} onClick={startNew}
          className="bg-gold text-ink-900 hover:bg-gold-light">
          <Plus className="mr-2 h-4 w-4" /> New manifest
        </Button>
      </div>

      {notice && <p data-testid="airport-notice" className="text-sm text-emerald-400">{notice}</p>}
      {error && <p data-testid="airport-error" className="text-sm text-red-400">{error}</p>}

      {blocked && (
        <div data-testid="am-quota-banner" className="rounded-xl border border-gold/30 bg-gold/10 p-5">
          <p className="text-sm text-gold">
            You've used all {quota.allowance} airport manifests this month.
          </p>
          <p className="mt-1 text-xs text-slate-300">
            Unlock {quota.topup_manifests} more for ${quota.topup_price} — or upgrade your plan for unlimited manifests.
          </p>
          {!isDriver && (
            <Button data-testid="am-topup" disabled={busy} onClick={() => act(async () => {
              const { data } = await api.post("/airport-manifests/topup-checkout",
                { origin_url: window.location.origin });
              if (!openCheckout(data.checkout_url, setCheckoutUrl)) setCheckoutUrl(data.checkout_url);
              return data;
            })} className="mt-4 bg-gold text-ink-900 hover:bg-gold-light">
              <Unlock className="mr-2 h-4 w-4" /> Unlock {quota.topup_manifests} for ${quota.topup_price}
            </Button>
          )}
          {checkoutUrl && (
            <a data-testid="am-checkout-link" href={checkoutUrl} target="_blank" rel="noreferrer"
              className="mt-3 block text-sm text-gold underline">
              Your secure Stripe checkout is ready — tap here to open it in a new tab.
            </a>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-ink-600 bg-ink-800">
        {items.length === 0 && (
          <div className="px-5 py-12 text-center">
            <Plane className="mx-auto h-6 w-6 text-slate-600" />
            <p data-testid="am-empty" className="mt-3 text-sm text-slate-500">
              No airport manifests yet. Create your first one.
            </p>
          </div>
        )}
        {items.map((m) => (
          <div key={m.id} data-testid={`am-row-${m.number}`}
            className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-600 px-5 py-4 last:border-0">
            <button onClick={() => openManifest(m.id)} className="min-w-0 flex-1 text-left">
              <p className="font-mono text-sm text-gold">#{m.number}</p>
              <p className="mt-1 truncate text-sm text-slate-100">
                {m.passenger_name} · {m.driver_name}
              </p>
              <p className="truncate text-xs text-slate-500">
                {m.trip_date} {m.pickup_time} · {[m.airline, m.flight_number].filter(Boolean).join(" ")} · {m.pickup_address}
              </p>
            </button>
            <div className="flex items-center gap-3">
              <span className="font-display text-base text-emerald-300">{money(m.driver_pay)}</span>
              <span data-testid={`am-sent-${m.number}`} className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {m.sent_at ? "emailed" : "not sent"}
              </span>
              {!isDriver && (
                <button data-testid={`am-delete-${m.number}`}
                  onClick={() => window.confirm(`Delete airport manifest #${m.number}? This cannot be undone.`)
                    && act(() => api.delete(`/airport-manifests/${m.id}`), "Manifest deleted.")}
                  className="text-slate-500 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
